import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { useIntl } from 'react-intl';
import translations from './agreementtable.psa.i18n';
import {
  AvtalestatusDto,
  PraksisstedavtaleDto,
} from '../api/generated/praksissted-generated-api';
import { formatLocaleNo, sortDates } from '../date.utils';
import useTableSearch from '../components/table/useTableSearch';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import { EmptyState } from '../components/emptyState/emptyState';
import Button from '../components/button';
import {
  useTableSortState,
  useTableSortCallback,
  useTableSortCompare,
} from '../components/table/useTableSort';
import { useNavigate } from 'react-router-dom';

const TableHeaders = {
  Avtalenavn: 'Avtalenavn',
  Institusjon: 'Institusjon',
  Ansvarlig: 'Ansvarlig',
  Status: 'Status',
  Startdato: 'Startdato',
  Sluttdato: 'Sluttdato',
  Handling: 'Handling',
} as const;

type TableHeaderToString = {
  [key in keyof typeof TableHeaders]: string;
};

type TableHeadersForSearch = Pick<
  typeof TableHeaders,
  'Avtalenavn' | 'Ansvarlig' | 'Institusjon'
>;

type TableHeaderForSearch = Pick<
  TableHeaderToString,
  keyof TableHeadersForSearch
>;

const TableHeadersSort = [
  TableHeaders.Avtalenavn,
  TableHeaders.Ansvarlig,
  TableHeaders.Institusjon,
  TableHeaders.Status,
  TableHeaders.Startdato,
  TableHeaders.Sluttdato,
] as const;

const AgreementTableWrapper = () => {
  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { formatMessage } = useIntl();

  const { data: avtaler, loading } = useLoader(
    () => praksisstedApi.avtalerPraksisGet(),
    [],
  );

  const labels = useMemo(
    () => ({
      avtaleEmptyStateTitle: formatMessage({
        id: translations.avtaleEmptyStateTitle.id,
      }),
      avtaleEmptyStateText: formatMessage({
        id: translations.avtaleEmptyStateText.id,
      }),
    }),
    [formatMessage],
  );

  if (loading) return <Spinner />;

  return avtaler?.avtaler ? (
    <AgreementTable avtaler={avtaler.avtaler} />
  ) : (
    <EmptyState
      title={labels.avtaleEmptyStateTitle}
      text={labels.avtaleEmptyStateText}
    />
  );
};

interface AgreementTableProps {
  avtaler: PraksisstedavtaleDto[];
}

const AgreementTable: React.FC<
  React.PropsWithChildren<AgreementTableProps>
> = ({ avtaler }) => {
  const navigate = useNavigate();
  const [searchTerms, setSearchTerms] = useState<TableHeaderForSearch>({
    Ansvarlig: '',
    Avtalenavn: '',
    Institusjon: '',
  });

  const { formatMessage, locale } = useIntl();

  const labelsWithoutHeaders = useMemo(
    () => ({
      aktivStatus: formatMessage({ id: translations.aktivStatus.id }),
      inaktivStatus: formatMessage({ id: translations.inaktivStatus.id }),
      avsluttetStatus: formatMessage({ id: translations.avsluttetStatus.id }),
      klarForSigneringStatus: formatMessage({
        id: translations.klarForSigneringStatus.id,
      }),
      ikkeKlarForSigneringStatus: formatMessage({
        id: translations.ikkeKlarForSigneringStatus.id,
      }),
      visAvtale: formatMessage({
        id: translations.visAvtale.id,
      }),
      ingen: formatMessage({
        id: translations.ingen.id,
      }),
    }),
    [formatMessage],
  );

  const translateStatusToLabel = useCallback(
    (status: AvtalestatusDto) => {
      switch (status) {
        case AvtalestatusDto.Avsluttet:
          return labelsWithoutHeaders.avsluttetStatus;
        case AvtalestatusDto.SendtTilSignering:
          return labelsWithoutHeaders.klarForSigneringStatus;
        case AvtalestatusDto.Aktiv:
          return labelsWithoutHeaders.aktivStatus;
        case AvtalestatusDto.IkkeAktiv:
          return labelsWithoutHeaders.inaktivStatus;
        default:
          return labelsWithoutHeaders.ikkeKlarForSigneringStatus;
      }
    },
    [labelsWithoutHeaders],
  );

  const headersWithTranslations: TableHeaderToString = useMemo(
    () => ({
      Ansvarlig: formatMessage({ id: translations.ansvarligHeader.id }),
      Avtalenavn: formatMessage({ id: translations.avtalenavnHeader.id }),
      Handling: formatMessage({ id: translations.handlingHeader.id }),
      Institusjon: formatMessage({ id: translations.institusjonHeader.id }),
      Sluttdato: formatMessage({ id: translations.sluttdatoHeader.id }),
      Startdato: formatMessage({ id: translations.startdatoHeader.id }),
      Status: formatMessage({ id: translations.statusHeader.id }),
    }),
    [formatMessage],
  );

  const initActiveCol = useMemo(
    () => Object.keys(TableHeaders).indexOf(TableHeaders.Avtalenavn),
    [],
  );

  const [activeCol, setActiveCol] = useState(initActiveCol);

  const compare = useTableSortCompare(locale);
  const sort = useCallback(
    (col: number, a: PraksisstedavtaleDto, b: PraksisstedavtaleDto) => {
      switch (Object.keys(TableHeaders)[col]) {
        case TableHeaders.Avtalenavn:
          return compare(a.avtalenavn || '', b.avtalenavn || '');
        case TableHeaders.Institusjon:
          return compare(
            a.avtaleeier.avtalestedforelder?.navn || '',
            b.avtaleeier.avtalestedforelder?.navn || '',
          );
        case TableHeaders.Ansvarlig:
          return compare(a.saksansvarlig || '', b.saksansvarlig || '');
        case TableHeaders.Startdato:
          return sortDates(a.avtalestart, b.avtalestart);
        case TableHeaders.Sluttdato:
          return sortDates(a.avtaleslutt, b.avtaleslutt);
        case TableHeaders.Status:
          return compare(
            translateStatusToLabel(a.avtalestatus),
            translateStatusToLabel(b.avtalestatus),
          );
      }
      return 0;
    },
    [compare, translateStatusToLabel],
  );

  const sortAvtaler = useTableSortCallback(sort);

  const [sortState, setSortState] = useTableSortState({
    col: initActiveCol,
    ascending: false,
  });

  const filteredAvtaler = useTableSearch(avtaler, [
    [
      searchTerms[TableHeaders.Avtalenavn],
      (avtale: PraksisstedavtaleDto) => avtale.avtalenavn || '',
    ],
    [
      searchTerms[TableHeaders.Institusjon],
      (avtale: PraksisstedavtaleDto) =>
        avtale.avtaleeier.avtalestedforelder?.navn || '',
    ],

    [
      searchTerms[TableHeaders.Ansvarlig],
      (avtale: PraksisstedavtaleDto) => avtale.saksansvarlig || '',
    ],
  ]);

  const getRows = useCallback(
    (avtalerForRows: PraksisstedavtaleDto[]) =>
      avtalerForRows.map((avtale) => {
        const {
          avtalenavn,
          saksansvarlig,
          avtaleeier: { avtalestedforelder },
          avtalestart,
          avtaleslutt,
          avtalestatus,
          avtaleId,
        } = avtale;

        return (
          <TableRow key={`agreementRow-${avtaleId}`}>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Avtalenavn]}
            >
              {avtalenavn}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Institusjon]}
            >
              {avtalestedforelder?.navn}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Ansvarlig]}
            >
              {saksansvarlig}
            </TableCellTd>
            <TableCellTd header={headersWithTranslations[TableHeaders.Status]}>
              {translateStatusToLabel(avtalestatus)}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Startdato]}
            >
              {avtalestart
                ? formatLocaleNo(avtalestart)
                : labelsWithoutHeaders.ingen}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Sluttdato]}
            >
              {avtaleslutt
                ? formatLocaleNo(avtaleslutt)
                : labelsWithoutHeaders.ingen}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Handling]}
            >
              <Button.Secondary
                onClick={() =>
                  navigate(`/myorganization/agreements/${avtaleId}`)
                }
              >
                {labelsWithoutHeaders.visAvtale}
              </Button.Secondary>
            </TableCellTd>
          </TableRow>
        );
      }),
    [
      navigate,
      translateStatusToLabel,
      labelsWithoutHeaders,
      headersWithTranslations,
    ],
  );

  const headers = useMemo(
    () =>
      Object.keys(TableHeaders).map((type, index) => (
        <TableCellTh
          key={`${type}-${index}`}
          colIndex={index}
          activeCol={index === activeCol}
          setActiveCol={setActiveCol}
          sort={(col, ascending) => setSortState({ col, ascending })}
          canSort={(TableHeadersSort as readonly string[]).includes(type)}
          search={(_col, value) =>
            setSearchTerms({ ...searchTerms, [type]: value })
          }
          canSearch={Object.keys(searchTerms).includes(type)}
        >
          {headersWithTranslations[type as keyof typeof TableHeaders]}
        </TableCellTh>
      )),
    [
      headersWithTranslations,
      activeCol,
      setSearchTerms,
      searchTerms,
      setSortState,
    ],
  );

  const rows = useMemo(
    () =>
      getRows(sortAvtaler(sortState.col, sortState.ascending, filteredAvtaler)),
    [filteredAvtaler, sortState, sortAvtaler, getRows],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

export default AgreementTableWrapper;
